import { useState, ChangeEvent } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2 } from "lucide-react";
import { css } from "js-beautify";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function CssFormatter() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [indent, setIndent] = useState(2);
  const [copied, setCopied] = useState(false);

  const formatCss = (val: string, spaces: number) => {
    if (!val.trim()) {
      setOutput("");
      return;
    }
    const formatted = css(val, { indent_size: spaces });
    setOutput(formatted);
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    formatCss(val, indent);
  };

  const handleIndentChange = (spaces: number) => {
    setIndent(spaces);
    formatCss(input, spaces);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="CSS Formatter Online"
      description="Format and beautify your CSS code instantly with our free online CSS formatter tool."
      h1="CSS Formatter"
      category="Dev Tools"
      categoryPath="/dev-tools"
      howToUse={[
        "Paste your unformatted CSS into the input area.",
        "Choose the desired indentation size (2 or 4 spaces).",
        "The formatted CSS will appear in the output area."
      ]}
      faq={[
        { question: "Is this CSS formatter free?", answer: "Yes, it is 100% free to format your CSS." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-slate-700">Indent:</span>
          <div className="flex gap-2">
            {[2, 4].map((spaces) => (
              <Button
                key={spaces}
                onClick={() => handleIndentChange(spaces)}
                variant={indent === spaces ? "default" : "outline"}
                size="sm"
              >
                {spaces} Spaces
              </Button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Input CSS</label>
            <Textarea
              placeholder="body{margin:0;padding:0;}"
              className="min-h-[400px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Formatted CSS</label>
            <Textarea
              className="min-h-[400px] bg-slate-50"
              value={output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
